// Anruf von einer Nummer ohne Lead: sucht die Nummer in Leads und Kontakten. Treffer lassen sich öffnen oder
// zurückrufen (die Telefonie legt den Anruf immer an einem Lead ab); der Rückruf erledigt den Eintrag, sobald es klingelt.
// Ohne Treffer legt „Lead anlegen und zurückrufen“ den Lead aus der Nummer an und wählt gleich; Kopieren bleibt daneben.
import { useEffect, useState } from "react";
import { sucheApi, type Aufgabe, type SuchTreffer } from "../../api";
import { kopieren } from "../../werkzeuge/tasten";
import { StatusPille } from "../Bausteine";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { useAnrufen } from "../Telefonnummer";
import { LeadAusNummer } from "./LeadAusNummer";
import { rueckrufMerken } from "./rueckruf";
import { mitArt, nummerText } from "./typen";
import "./inbox.css";

interface Props {
  aufgabe: Aufgabe;
  onLead: (leadId: string) => void;
  onSchliessen: () => void;
}

export function NummerPanel({ aufgabe: a, onLead, onSchliessen }: Props) {
  const nummer = mitArt(a).phone ?? "";
  const [treffer, setTreffer] = useState<SuchTreffer[] | null>(null);
  const [fehler, setFehler] = useState(false);
  const anrufen = useAnrufen();
  const meldung = useMeldung();

  useEffect(() => {
    const abbruch = new AbortController();
    setTreffer(null);
    setFehler(false);
    sucheApi
      .suchen(nummer, 8, abbruch.signal)
      .then((r) => {
        const gesehen = new Set<string>();
        setTreffer(r.data.filter((t) => (gesehen.has(t.lead_id) ? false : (gesehen.add(t.lead_id), true))));
      })
      .catch(() => {
        if (!abbruch.signal.aborted) setFehler(true);
      });
    return () => abbruch.abort();
  }, [nummer]);

  return (
    <div className="nummer-panel" role="region" aria-label={`Nummer ${nummerText(a)}`}>
      <div className="nummer-panel__kopf">
        <span>
          Leads und Kontakte mit <strong>{nummerText(a)}</strong>
        </span>
        <button type="button" className="symbolknopf" title="Schließen" aria-label="Schließen" onClick={onSchliessen}>
          <Icon name="schliessen" groesse={14} />
        </button>
      </div>
      {fehler ? (
        <div className="nummer-panel__leer">Suche nicht möglich.</div>
      ) : treffer === null ? (
        <div className="nummer-panel__leer">Suche …</div>
      ) : treffer.length === 0 ? (
        <div className="nummer-panel__leer">
          <span>Kein Lead mit dieser Nummer. Angerufen wird immer an einem Lead.</span>
          <LeadAusNummer
            nummer={nummer}
            anzeige={nummerText(a)}
            onAngelegt={(lead, kontaktName) => {
              rueckrufMerken(a.id, lead.id);
              void anrufen(nummer, lead.id, null, { leadName: lead.name, kontaktName });
            }}
          />
          <button type="button" className="knopf knopf--klein" onClick={() => void kopieren(nummer).then((ok) => ok && meldung.ok("Nummer kopiert."))}>
            <Icon name="kopieren" groesse={13} /> Nummer kopieren
          </button>
        </div>
      ) : (
        <ul className="nummer-panel__liste">
          {treffer.map((t) => {
            // Bei Kontakt-Treffern steht in subtitle der Lead; der Lead ist der Name, der Kontakt der Zusatz
            const lead = t.type === "contact" ? (t.subtitle ?? t.title) : t.title;
            const zusatz = t.type === "contact" ? t.title : t.subtitle;
            return (
            <li key={`${t.lead_id}-${t.contact_id ?? ""}`} className="nummer-panel__zeile">
              <button type="button" className="nummer-panel__name" onClick={() => onLead(t.lead_id)}>
                {lead}
              </button>
              {zusatz && zusatz !== lead ? <span className="leise">{zusatz}</span> : null}
              <StatusPille label={t.status_label} klein />
              <button type="button" className="knopf knopf--klein" onClick={() => {
                  rueckrufMerken(a.id, t.lead_id);
                  void anrufen(nummer, t.lead_id, t.contact_id, { leadName: lead });
                }}>
                <Icon name="telefon" groesse={13} /> Anrufen
              </button>
              <button type="button" className="knopf knopf--klein" onClick={() => onLead(t.lead_id)}>
                Öffnen
              </button>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
